import React, { useState, useRef, useEffect, useCallback } from 'react';
import { editImage, reframeImage, generateSceneImage, blobToBase64, transcribeDialogueAudio } from '../services/geminiService';
import { getModelForCapability } from '../utils/modelHelper';
import { CloseIcon } from './icons/CloseIcon';
import { LoadingSpinner } from './icons/LoadingSpinner';
import { PlusIcon } from './icons/PlusIcon';
import { MagicWandIcon } from './icons/MagicWandIcon';
import { CropIcon } from './icons/CropIcon';
import { MicrophoneIcon } from './icons/MicrophoneIcon';
import { PencilIcon } from './icons/PencilIcon';
import { UndoIcon } from './icons/UndoIcon';
import { RedoIcon } from './icons/RedoIcon';
import { DownloadIcon } from './icons/DownloadIcon';
import { CREDIT_COSTS } from '../types';

const Switch = ({ id, checked, onChange }) => (
    <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`${checked ? 'bg-purple-600' : 'bg-gray-600'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
    >
        <span className={`${checked ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
    </button>
);

export const ImageEditorModal = ({ shot, characters, projectData, frameType, onClose, onSave, onDeductCredits, onTrackCreditsUsed, onSaveMedia }) => {
    const initialImage = frameType === 'start' ? shot.startFrameImage : shot.endFrameImage;
    
    // History state for undo/redo
    const [history, setHistory] = useState(initialImage ? [initialImage] : []);
    const [historyIndex, setHistoryIndex] = useState(initialImage ? 0 : -1);
    const currentImage = history[historyIndex] ?? null;
    
    const [prompt, setPrompt] = useState(frameType === 'start' ? (shot.startFramePrompt || shot.description || '') : (shot.endFramePrompt || shot.description || ''));
    const [editPrompt, setEditPrompt] = useState('');
    const [editorStatus, setEditorStatus] = useState('idle');
    const isLoading = editorStatus !== 'idle';
    const [error, setError] = useState(null);
    const [useStartFrameAsRef, setUseStartFrameAsRef] = useState(true);
    const [referenceImages, setReferenceImages] = useState([]);
    const fileInputRef = useRef(null);
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [micError, setMicError] = useState(null);

    // Masking state
    const [maskingEnabled, setMaskingEnabled] = useState(false);
    const [brushSize, setBrushSize] = useState(30);
    const [drawingMode, setDrawingMode] = useState('brush');
    const [isDrawing, setIsDrawing] = useState(false);

    // Cropping state
    const [crop, setCrop] = useState(null);
    const [dragHandle, setDragHandle] = useState(null);
    const [dragStart, setDragStart] = useState(null);

    const imageRef = useRef(null);
    const drawingCanvasRef = useRef(null);
    const lastPoint = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    
    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const addNewImageState = (newImage) => {
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(newImage);
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
    };

    const handleUndo = () => {
        if (historyIndex > 0) {
            setHistoryIndex(historyIndex - 1);
        }
    };

    const handleRedo = () => {
        if (historyIndex < history.length - 1) {
            setHistoryIndex(historyIndex + 1);
        }
    };

    const handleCanvasResize = useCallback(() => {
        const image = imageRef.current;
        const canvas = drawingCanvasRef.current;
        if (image && canvas) {
            const { width, height } = image.getBoundingClientRect();
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;

            const scale = image.naturalWidth / width;
            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;
        }
    }, []);

    useEffect(() => {
        handleCanvasResize();
        const imageEl = imageRef.current;
        if (!imageEl) return;
        
        const resizeObserver = new ResizeObserver(handleCanvasResize);
        resizeObserver.observe(imageEl);
        
        return () => resizeObserver.disconnect();
    }, [currentImage, handleCanvasResize]);


    const getMousePos = (e) => {
        const canvas = drawingCanvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        return {
            x: (e.clientX - rect.left) * (canvas.width / rect.width),
            y: (e.clientY - rect.top) * (canvas.height / rect.height)
        };
    };

    const drawLine = (x0, y0, x1, y1) => {
        const ctx = drawingCanvasRef.current?.getContext('2d');
        if (!ctx) return;

        ctx.lineWidth = brushSize * (drawingCanvasRef.current.width / drawingCanvasRef.current.getBoundingClientRect().width);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        if (drawingMode === 'brush') {
            ctx.strokeStyle = 'rgba(239, 68, 68, 0.7)';
            ctx.globalCompositeOperation = 'source-over';
        } else {
            ctx.strokeStyle = 'rgba(0, 0, 0, 1)';
            ctx.globalCompositeOperation = 'destination-out';
        }

        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.stroke();
    };

    const handleMouseDown = (e) => {
        if (!maskingEnabled) return;
        const pos = getMousePos(e);
        if (pos) {
            setIsDrawing(true);
            lastPoint.current = pos;
        }
    };
    
    const handleMouseMove = (e) => {
        if (!isDrawing || !maskingEnabled) return;
        const pos = getMousePos(e);
        if (pos && lastPoint.current) {
            drawLine(lastPoint.current.x, lastPoint.current.y, pos.x, pos.y);
            lastPoint.current = pos;
        }
    };

    const handleMouseUp = () => {
        setIsDrawing(false);
        lastPoint.current = null;
    };

    const handleClearMask = () => {
        const canvas = drawingCanvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            ctx?.clearRect(0, 0, canvas.width, canvas.height);
        }
    };

    const handleStartCrop = () => {
        const image = imageRef.current;
        if (!image) return;
        setMaskingEnabled(false);
        const { clientWidth, clientHeight } = image;
        const initialWidth = clientWidth * 0.8;
        const initialHeight = clientHeight * 0.8;
        setCrop({
            x: (clientWidth - initialWidth) / 2,
            y: (clientHeight - initialHeight) / 2,
            width: initialWidth,
            height: initialHeight,
        });
    };

    const handleCancelCrop = () => setCrop(null);

    const handleApplyCrop = () => {
        const image = imageRef.current;
        if (!image || !image.src || !crop) return;
        
        const canvas = document.createElement('canvas');
        const scaleX = image.naturalWidth / image.clientWidth;
        const scaleY = image.naturalHeight / image.clientHeight;

        const sourceX = crop.x * scaleX;
        const sourceY = crop.y * scaleY;
        const sourceWidth = crop.width * scaleX;
        const sourceHeight = crop.height * scaleY;

        canvas.width = sourceWidth;
        canvas.height = sourceHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        ctx.drawImage(
            image,
            sourceX, sourceY, sourceWidth, sourceHeight,
            0, 0, sourceWidth, sourceHeight
        );
        
        addNewImageState(canvas.toDataURL('image/png'));
        setCrop(null);
    };
    
    const handleCropMouseDown = (e, handle) => {
        e.preventDefault();
        e.stopPropagation();
        if (!crop) return;
        setDragHandle(handle);
        setDragStart({ x: e.clientX, y: e.clientY, crop });
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!dragStart || !crop || !dragHandle) return;
            const dx = e.clientX - dragStart.x;
            const dy = e.clientY - dragStart.y;
            
            let newCrop = { ...crop };
            
            if (dragHandle === 'move') {
                newCrop.x = dragStart.crop.x + dx;
                newCrop.y = dragStart.crop.y + dy;
            }
            if (dragHandle.includes('e')) newCrop.width = dragStart.crop.width + dx;
            if (dragHandle.includes('w')) {
                newCrop.width = dragStart.crop.width - dx;
                newCrop.x = dragStart.crop.x + dx;
            }
            if (dragHandle.includes('s')) newCrop.height = dragStart.crop.height + dy;
            if (dragHandle.includes('n')) {
                newCrop.height = dragStart.crop.height - dy;
                newCrop.y = dragStart.crop.y + dy;
            }

            if (newCrop.width < 20) {
                 if (dragHandle.includes('w')) newCrop.x = crop.x + crop.width - 20;
                 newCrop.width = 20;
            }
            if (newCrop.height < 20) {
                if (dragHandle.includes('n')) newCrop.y = crop.y + crop.height - 20;
                newCrop.height = 20;
            }
            setCrop(newCrop);
        };
        const handleMouseUp = () => setDragStart(null);

        if (dragStart) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [dragStart, crop, dragHandle]);


    const handleMicClick = async () => {
        if (isRecording) {
            if (mediaRecorderRef.current) {
                mediaRecorderRef.current.stop();
            }
            setIsRecording(false);
        } else {
            setMicError(null);
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                const mediaRecorder = new MediaRecorder(stream);
                mediaRecorderRef.current = mediaRecorder;
                audioChunksRef.current = [];

                mediaRecorder.ondataavailable = (event) => {
                    audioChunksRef.current.push(event.data);
                };

                mediaRecorder.onstop = async () => {
                    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                    stream.getTracks().forEach(track => track.stop());

                    if (audioBlob.size === 0) {
                        setMicError("No audio was recorded.");
                        return;
                    }

                    setIsTranscribing(true);
                    setMicError(null);

                    try {
                        const base64Audio = await blobToBase64(audioBlob);
                        const transcript = await transcribeDialogueAudio(base64Audio);
                        setEditPrompt(prev => (prev ? `${prev} ${transcript}` : transcript).trim());
                    } catch (err) {
                        console.error("Transcription error:", err);
                        const errorMessage = err instanceof Error ? err.message : "Transcription failed.";
                        setMicError(errorMessage);
                    } finally {
                        setIsTranscribing(false);
                    }
                };

                mediaRecorder.start();
                setIsRecording(true);
            } catch (err) {
                console.error("Mic access error:", err);
                setMicError("Microphone access denied. Please enable it in browser settings.");
            }
        }
    };


    const handleFileChange = (e) => {
        const files = e.target.files;
        if (!files) return;

        Array.from(files).forEach(file => {
            if (file instanceof Blob) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setReferenceImages(prev => [...prev, reader.result]);
                };
                reader.readAsDataURL(file);
            }
        });
    };
    
    const handleRemoveReference = (index) => {
        setReferenceImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleGenerate = async () => {
        // Credits are deducted by backend - removed upfront deduction

        setEditorStatus('generating');
        setError(null);
        try {
            let result;
            // Get selected model for image generation
            let modelId = getModelForCapability('professional_image') || getModelForCapability('fast_image');
            
            if (frameType === 'end' && useStartFrameAsRef && shot.startFrameImage) {
                const allRefs = [shot.startFrameImage, ...referenceImages];
                modelId = getModelForCapability('reference_image') || modelId;
                
                const actionPrompt = `Generate the END FRAME of a video shot, based on the description: "${prompt}".
                
                REFERENCE IMAGE CONTEXT:
                - The provided reference image is the START FRAME (t=0s).
                - The image you generate is the END FRAME (t=5s).
                
                CRITICAL INSTRUCTIONS:
                1. DYNAMIC CHANGE: The subject MUST have moved or the camera MUST have moved according to the description.
                2. VISUAL CONSISTENCY: Keep the identity (clothing, face), location, and lighting consistent with the start frame reference.
                3. AVOID DUPLICATION: Do NOT output the same image. The pose, angle, or position MUST be different to represent the passage of time.`;

                result = await generateSceneImage(actionPrompt, projectData.aspectRatio, characters, allRefs, modelId);
            } else {
                const hasRefs = referenceImages.length > 0;
                modelId = hasRefs ? (getModelForCapability('reference_image') || modelId) : modelId;
                result = await generateSceneImage(prompt, projectData.aspectRatio, characters, referenceImages, modelId);
            }
            
            // Handle both old format (string) and new format (object with credits)
            const newImage = result?.image || result;
            addNewImageState(newImage);
            
            // Track credits from backend response
            if (onTrackCreditsUsed && result?.creditsUsed) {
                onTrackCreditsUsed(result.creditsUsed, result.creditsRemaining, result.jobId);
            }
        } catch (err) {
            console.error(err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to generate image. Please try a different prompt.';
            setError(errorMessage);
        } finally {
            setEditorStatus('idle');
        }
    };

    const handleEdit = async () => {
        if (!currentImage || !editPrompt.trim()) return;
        // Credits are deducted by backend - removed upfront deduction

        setEditorStatus('editing');
        setError(null);
        let maskToUse = undefined;

        const combinedPrompt = `${prompt}. ${editPrompt}`;

        if (maskingEnabled && drawingCanvasRef.current) {
            const canvas = drawingCanvasRef.current;
            const offscreenCanvas = document.createElement('canvas');
            offscreenCanvas.width = canvas.width;
            offscreenCanvas.height = canvas.height;
            const offscreenCtx = offscreenCanvas.getContext('2d');
            const drawingCtx = canvas.getContext('2d');

            if (offscreenCtx && drawingCtx) {
                const sourceImageData = drawingCtx.getImageData(0, 0, canvas.width, canvas.height);
                const sourceData = sourceImageData.data;

                const maskImageData = drawingCtx.createImageData(canvas.width, canvas.height);
                const maskData = maskImageData.data;
                let hasMaskContent = false;

                for (let i = 0; i < sourceData.length; i += 4) {
                    if (sourceData[i + 3] > 0) {
                        maskData[i] = 255; maskData[i + 1] = 255; maskData[i + 2] = 255; maskData[i + 3] = 255;
                        hasMaskContent = true;
                    } else {
                        maskData[i] = 0; maskData[i + 1] = 0; maskData[i + 2] = 0; maskData[i + 3] = 255;
                    }
                }

                if (hasMaskContent) {
                    offscreenCtx.putImageData(maskImageData, 0, 0);
                    maskToUse = offscreenCanvas.toDataURL('image/png');
                }
            }
        }

        try {
            // Get selected model for image editing
            let modelId = getModelForCapability('reference_image') || getModelForCapability('professional_image') || getModelForCapability('fast_image');
            const result = await editImage(currentImage, combinedPrompt, characters, referenceImages, maskToUse, modelId);
            
            // Handle both old format (string) and new format (object with credits)
            const newImage = result?.imageUrl || result;
            addNewImageState(newImage);
            setPrompt(combinedPrompt);
            setEditPrompt('');
            handleClearMask();
            
            // Track credits from backend response
            if (onTrackCreditsUsed && result?.creditsUsed) {
                onTrackCreditsUsed(result.creditsUsed, result.creditsRemaining, result.jobId);
            }
        } catch (err) {
            console.error(err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to edit image. Please try a different prompt.';
            setError(errorMessage);
        } finally {
            setEditorStatus('idle');
        }
    };

    const handleReframe = async (aspectRatio) => {
        if (!currentImage) return;
        // Credits are deducted by backend - removed upfront deduction

        setEditorStatus('reframing');
        setError(null);
        try {
            const result = await reframeImage(currentImage, aspectRatio);
            
            // Handle both old format (string) and new format (object with credits)
            const newImage = result?.imageUrl || result?.image || result;
            addNewImageState(newImage);
            
            // Track credits from backend response
            if (onTrackCreditsUsed && result?.creditsUsed) {
                onTrackCreditsUsed(result.creditsUsed, result.creditsRemaining, result.jobId);
            }
        } catch (err) {
            console.error(err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to reframe image.';
            setError(errorMessage);
        } finally {
            setEditorStatus('idle');
        }
    };

    const handleSave = () => {
        if (currentImage) {
            onSave(currentImage);
            if (onSaveMedia) {
                onSaveMedia([currentImage]);
            }
        }
    };

    const handleDownload = () => {
        if (!currentImage) return;
        const link = document.createElement('a');
        link.href = currentImage;
        link.download = `loopai_frame_${shot.id}_${frameType}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const title = `Image Editor: ${frameType.charAt(0).toUpperCase() + frameType.slice(1)} Frame`;

    const renderCropHandles = () => {
        const handles = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw', 'move'];
        return handles.map(handle => (
            <div
                key={handle}
                className={`absolute bg-purple-500/80 border border-white/50 ${handle === 'move' ? 'cursor-move inset-2' : 'cursor-pointer'}`}
                style={{
                    ...(handle.includes('n') && { top: -4, height: 8 }),
                    ...(handle.includes('s') && { bottom: -4, height: 8 }),
                    ...(handle.includes('e') && { right: -4, width: 8 }),
                    ...(handle.includes('w') && { left: -4, width: 8 }),
                    ...(!handle.includes('n') && !handle.includes('s') && { top: 0, bottom: 0 }),
                    ...(!handle.includes('e') && !handle.includes('w') && { left: 0, right: 0 }),
                }}
                onMouseDown={e => handleCropMouseDown(e, handle)}
            />
        ));
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-[#181820] border border-gray-700 rounded-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center p-4 border-b border-gray-700">
                    <h2 className="text-xl font-bold">{title}</h2>
                    <div className="flex items-center gap-2">
                        <button onClick={handleUndo} disabled={historyIndex <= 0} className="p-2 rounded-full hover:bg-gray-700 disabled:opacity-30" title="Undo">
                            <UndoIcon className="w-5 h-5" />
                        </button>
                        <button onClick={handleRedo} disabled={historyIndex >= history.length - 1} className="p-2 rounded-full hover:bg-gray-700 disabled:opacity-30" title="Redo">
                            <RedoIcon className="w-5 h-5" />
                        </button>
                        <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-700">
                            <CloseIcon className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto flex">
                    <div className="flex-1 p-6 space-y-4">
                        <div className="space-y-3">
                            <label className="block text-sm font-medium">Generation Prompt</label>
                            <textarea
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                rows={3}
                                className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                                placeholder="Describe what you want to generate..."
                            />
                            
                            {frameType === 'end' && shot.startFrameImage && (
                                <div className="flex items-center gap-2">
                                    <Switch checked={useStartFrameAsRef} onChange={setUseStartFrameAsRef} />
                                    <label className="text-sm text-gray-400">Use start frame as reference</label>
                                </div>
                            )}
                            
                            <button
                                onClick={handleGenerate}
                                disabled={isLoading || !prompt.trim()}
                                className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white font-bold py-2 rounded-lg transition-colors flex items-center justify-center"
                            >
                                {editorStatus === 'generating' ? (
                                    <><LoadingSpinner className="w-4 h-4 mr-2" /> Generating...</>
                                ) : (
                                    <><MagicWandIcon className="w-4 h-4 mr-2" /> Generate Image</>
                                )}
                            </button>
                        </div>

                        {/* Reference Images */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium">Reference Images</label>
                            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" multiple className="hidden" />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full py-2 border border-dashed border-gray-600 rounded-lg hover:border-gray-500 flex items-center justify-center gap-2"
                            >
                                <PlusIcon className="w-4 h-4" />
                                Add Reference Image
                            </button>
                            {referenceImages.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {referenceImages.map((img, idx) => (
                                        <div key={idx} className="relative w-16 h-16">
                                            <img src={img} className="w-full h-full object-cover rounded" alt={`Ref ${idx + 1}`} />
                                            <button
                                                onClick={() => handleRemoveReference(idx)}
                                                className="absolute -top-2 -right-2 bg-red-500 rounded-full w-5 h-5 flex items-center justify-center text-xs"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Edit Section */}
                        {currentImage && (
                            <div className="space-y-3 pt-3 border-t border-gray-700">
                                <label className="block text-sm font-medium">Edit Instructions</label>
                                <div className="flex gap-2">
                                    <textarea
                                        value={editPrompt}
                                        onChange={(e) => setEditPrompt(e.target.value)}
                                        rows={2}
                                        className="flex-1 p-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                                        placeholder="e.g., Add rain, change lighting to sunset..."
                                    />
                                    <button
                                        onClick={handleMicClick}
                                        disabled={isTranscribing}
                                        className={`p-2 rounded-lg ${isRecording ? 'bg-red-500 animate-pulse' : 'bg-gray-700 hover:bg-gray-600'}`}
                                        title="Voice input"
                                    >
                                        <MicrophoneIcon className="w-5 h-5" />
                                    </button>
                                </div>
                                {(micError || isTranscribing) && (
                                    <p className="text-xs text-gray-400">{isTranscribing ? 'Transcribing...' : micError}</p>
                                )}
                                
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setMaskingEnabled(!maskingEnabled)}
                                        className={`flex-1 py-2 rounded-lg ${maskingEnabled ? 'bg-purple-600' : 'bg-gray-700'}`}
                                    >
                                        <PencilIcon className="w-4 h-4 inline mr-2" />
                                        {maskingEnabled ? 'Masking Active' : 'Enable Masking'}
                                    </button>
                                    <button
                                        onClick={handleStartCrop}
                                        disabled={maskingEnabled}
                                        className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded-lg"
                                    >
                                        <CropIcon className="w-4 h-4 inline mr-2" />
                                        Crop
                                    </button>
                                </div>

                                {maskingEnabled && (
                                    <div className="space-y-2">
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setDrawingMode('brush')}
                                                className={`flex-1 py-1 rounded ${drawingMode === 'brush' ? 'bg-purple-600' : 'bg-gray-700'}`}
                                            >
                                                Brush
                                            </button>
                                            <button
                                                onClick={() => setDrawingMode('eraser')}
                                                className={`flex-1 py-1 rounded ${drawingMode === 'eraser' ? 'bg-purple-600' : 'bg-gray-700'}`}
                                            >
                                                Eraser
                                            </button>
                                        </div>
                                        <input
                                            type="range"
                                            min="10"
                                            max="100"
                                            value={brushSize}
                                            onChange={(e) => setBrushSize(parseInt(e.target.value))}
                                            className="w-full"
                                        />
                                        <button onClick={handleClearMask} className="w-full py-1 bg-gray-700 hover:bg-gray-600 rounded text-sm">
                                            Clear Mask
                                        </button>
                                    </div>
                                )}
                                
                                <button
                                    onClick={handleEdit}
                                    disabled={isLoading || !editPrompt.trim()}
                                    className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 text-white font-bold py-2 rounded-lg transition-colors"
                                >
                                    {editorStatus === 'editing' ? 'Editing...' : '✨ Apply Edit'}
                                </button>
                            </div>
                        )}

                        {/* Reframe */}
                        {currentImage && (
                            <div className="space-y-2 pt-3 border-t border-gray-700">
                                <label className="block text-sm font-medium">Reframe</label>
                                <div className="flex gap-2">
                                    <button onClick={() => handleReframe('16:9')} disabled={isLoading} className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded">16:9</button>
                                    <button onClick={() => handleReframe('9:16')} disabled={isLoading} className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded">9:16</button>
                                    <button onClick={() => handleReframe('1:1')} disabled={isLoading} className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded">1:1</button>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="bg-red-500/20 border border-red-500 text-red-300 p-3 rounded-lg text-sm">
                                {error}
                            </div>
                        )}
                    </div>

                    {/* Preview */}
                    <div className="w-[500px] p-6 border-l border-gray-700">
                        <label className="block text-sm font-medium mb-2">Preview</label>
                        <div className="bg-gray-900 rounded-lg aspect-video flex items-center justify-center relative">
                            {currentImage ? (
                                <div className="relative w-full h-full">
                                    <img 
                                        ref={imageRef}
                                        src={currentImage} 
                                        alt="Frame" 
                                        className="w-full h-full object-contain rounded-lg" 
                                    />
                                    <canvas
                                        ref={drawingCanvasRef}
                                        className="absolute top-0 left-0 pointer-events-auto"
                                        onMouseDown={handleMouseDown}
                                        onMouseMove={handleMouseMove}
                                        onMouseUp={handleMouseUp}
                                        onMouseLeave={handleMouseUp}
                                    />
                                    {crop && (
                                        <div
                                            className="absolute border-2 border-purple-500"
                                            style={{
                                                left: crop.x,
                                                top: crop.y,
                                                width: crop.width,
                                                height: crop.height,
                                            }}
                                        >
                                            {renderCropHandles()}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-gray-500">No image generated yet</p>
                            )}
                        </div>
                        
                        {crop && (
                            <div className="mt-2 flex gap-2">
                                <button onClick={handleApplyCrop} className="flex-1 bg-purple-600 hover:bg-purple-700 py-2 rounded">Apply Crop</button>
                                <button onClick={handleCancelCrop} className="flex-1 bg-gray-700 hover:bg-gray-600 py-2 rounded">Cancel</button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex justify-between p-4 border-t border-gray-700">
                    <button onClick={handleDownload} disabled={!currentImage} className="bg-sky-600 hover:bg-sky-700 disabled:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg flex items-center">
                        <DownloadIcon className="w-4 h-4 mr-2" />
                        Download
                    </button>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-6 rounded-lg">
                            Cancel
                        </button>
                        <button onClick={handleSave} disabled={!currentImage} className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white font-bold py-2 px-6 rounded-lg">
                            Save Image
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
